import { Box, Divider, InputAdornment, TextField, Typography } from '@mui/material';
import { Fragment } from 'react';
import { toast } from 'sonner';
import CheckBoxV4 from '../../components/CheckBoxV4';
import { HoverTaxTooltip } from '../../utility/helpers/commonFunction';
import { NumericEngine } from '../../utility/helpers/numericEngine';
import { DOCUMENT_TAX_TYPE, formatTaxPercent, getTaxRowRate, taxPercentInputRegex, RATE_KEY } from '../../utility/helpers/taxCalculation';
import { useTaxDecimalPrecision } from '../../utility/hooks/useTaxDecimalPrecision';
import TaxBreakdownCard from '../Tax/TaxBreakdownCard';

const fmtMoney = (n) => (Number(n) > 0 ? NumericEngine.format(n, { formatCurrency: true, trailingZeros: true }) : '$0.00');

// Editable rate input value: keep the raw string while typing; only round a pre-existing
// value that already has more decimals than the shop precision allows.
const displayRateInput = (value, precision) => {
    const s = value == null ? '' : String(value);
    if (s === '' || s.endsWith('.')) return s;
    const dec = s.split('.')[1];
    return dec && dec.length > precision ? formatTaxPercent(s, precision) : s;
};

const TAX_INPUT_SX = {
    height: '36px',
    width: '80px',

    '& .MuiInputBase-root': {
        height: '36px',
        width: '80px',
        borderRadius: '8px',
        padding: '0 9.5px 0 0',
    },

    '& .MuiInputBase-input': {
        width: '80px',
        padding: '6px 9px',
        paddingRight: 0,
    },
    '& .MuiInputAdornment-root': {
        marginLeft: '0!important',
    },
};

const DISABLED_TAX_INPUT_SX = {
    '& .MuiOutlinedInput-root': {
        backgroundColor: '#F5F5F5',
        border: '1px solid rgba(0,0,0,0.10)',
        opacity: 0.4,
    },
};

// Grid replaces the old per-row flex layout. Columns, left to right:
// tax name | checkbox | divider | rate input | dash | amount.
// Grid computes each column's width from the widest cell in THAT column
// across all rows, so every row's checkbox/divider/input/dash line up,
// and the amount column is exactly as wide as the longest amount.
const TAX_GRID_SX = {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto auto auto auto max-content',
    alignItems: 'center',
    columnGap: '8px',
    rowGap: '12px',
    width: '100%',
};

// Name cell: minWidth 0 lets long names truncate/wrap instead of blowing out the grid track,
// same role LEFT_SECTION_SX's flex:1 + minWidth:0 played before.
const TAX_NAME_CELL_SX = {
    minWidth: 0,
};

const TAX_NAME_SX = {
    cursor: 'pointer',
    fontSize: 16,
};

const CHECKBOX_SX = {
    p: 0,
    ml: 2,
};

const CHECKBOX_ICON_STYLE = {
    width: 20,
    height: 20,
    borderRadius: '4px',
    border: '2px solid #E6E6E6',
    backgroundColor: '#fff',
    display: 'inline-block',
    boxSizing: 'border-box',
    boxShadow: '0 2px 3px 0 rgba(0,0,0,0.06)',
};

const DIVIDER_SX = {
    mx: 1,
    borderColor: '#E0E0E0',
    backgroundColor: '#E0E0E0',
    width: '1px',
    height: '25px',
    alignSelf: 'center',
};

const RIGHT_SECTION_SX = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
};

// Non-editable rate label ("5%"): right-align within its own grid column so it
// stays consistent with the editable TextField's right-hand alignment.
const TAX_RATE_LABEL_SX = {
    justifySelf: 'end',
};

// Amount cell: justifySelf 'end' right-aligns every amount to the same edge of
// its grid column. Since the column width = widest amount in the list, the
// widest value sits flush against the dash (8px columnGap), while shorter
// values get pushed right, leaving extra space before them — exactly the
// "right edges align, gap only guaranteed for the widest value" behavior.
const TAX_AMOUNT_SX = {
    cursor: 'pointer',
    justifySelf: 'end',
    textAlign: 'right',
};

/** How each document refers to itself in the tax rows' copy. */
const DOCUMENT_LABEL = {
    [DOCUMENT_TAX_TYPE.INVOICE]: 'invoice',
    [DOCUMENT_TAX_TYPE.ESTIMATE]: 'estimate',
    [DOCUMENT_TAX_TYPE.WORK_ORDER]: 'work order',
};

const MultiTaxRows = ({
    taxesBreakdown = [],
    setTaxesBreakdown,
    documentType = DOCUMENT_TAX_TYPE.INVOICE,
    editable = true,
    taxPercentageInputRef,
    lineTaxSummary = [],
    onBulkToggle,
    hideCheckbox = false,
}) => {
    // Each document has its own rate field on a tax rule — never assume the invoice's.
    const rateKey = RATE_KEY[documentType] || RATE_KEY[DOCUMENT_TAX_TYPE.INVOICE];
    const documentLabel = DOCUMENT_LABEL[documentType] || 'invoice';
    const precision = useTaxDecimalPrecision();
    const summaryMap = new Map((lineTaxSummary || []).map((s) => [s.taxId, s]));

    // The summary checkbox is a BULK toggle over the line items, not a master gate. Toggling it
    // materialises the change onto every line (onBulkToggle); the row's applyTax is then derived from
    // the lines. Falls back to the old direct set only when no bulk handler is wired (read-only views).
    const handleApplyTaxChange = (index, checked) => {
        const taxId = taxesBreakdown[index]?.taxId;
        if (onBulkToggle && taxId != null) {
            onBulkToggle(taxId, checked);
            return;
        }
        if (!setTaxesBreakdown) return;
        setTaxesBreakdown((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], applyTax: checked };
            return updated;
        });
    };

    const handleRateChange = (index, inputValue) => {
        if (!setTaxesBreakdown) return;

        let numericValue = inputValue.replace(/[^\d.]/g, '');
        if (numericValue.length > 1 && numericValue.startsWith('0') && /[1-9a-zA-Z]/.test(numericValue[1])) {
            numericValue = numericValue.slice(1);
        }

        if (!(numericValue === '' || taxPercentInputRegex(precision).test(numericValue))) return;

        const taxPercentage = Number(numericValue) || 0;
        if (taxPercentage >= 100) {
            toast.error('Please enter a valid tax percentage (0-99)');
            return;
        }

        setTaxesBreakdown((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [rateKey]: numericValue };
            return updated;
        });
    };

    if (!taxesBreakdown?.length) return null;

    // Ref target for "Adjust Tax" focus: first enabled (applyTax) row so focus lands on a
    // typeable field even when the first row is unchecked/disabled. Falls back to row 0.
    const firstEditableIndex = taxesBreakdown.findIndex((r) => r.applyTax);
    const refIndex = firstEditableIndex === -1 ? 0 : firstEditableIndex;

    // Sum of the per-tax amounts shown above — matches the invoice tax total.
    const totalTax = taxesBreakdown.reduce((acc, r) => NumericEngine.add(acc, String(r.taxAmount || 0)), '0');
    return (
        <Box sx={TAX_GRID_SX}>
            {taxesBreakdown.map((row, index) => {
                const isDisabled = editable && !row.applyTax;
                const sum = summaryMap.get(row.taxId);
                const customized = editable && !!sum?.customized;

                // Same detail card used for both trigger points below — kept as a
                // single source of truth so the two tooltips can never drift out of sync.
                // Shared with the read-only detail summaries via TaxSummaryHover.
                // remove this condition customized && sum &&  as nedd for client requirement for hover on hover over tax calculation or tax label
                // name/rate come from the live global row, not `sum` — the user may be mid-edit on
                // the rate, and the row holds the raw input string while the summary holds a Number.
                const customizedTooltipContent = <TaxBreakdownCard summary={sum} name={row.name} rate={getTaxRowRate(row, documentType)} />;

                // Checkbox-only tooltip shown when the row is disabled because the tax was REMOVED
                // from individual line items (see summarizeTaxAcrossLines — only a removal locks).
                // Same padding/width as customizedTooltipContent so both tooltips share one visual
                // style, just different content.
                const disabledCheckboxTooltipContent = (
                    <Box sx={{ p: '10px 12px', width: 185 }}>
                        <Typography sx={{ fontSize: 12, color: '#212121', lineHeight: '150%' }}>
                            Tax was edited from individual {documentLabel} lines. Update line items to change this total.
                        </Typography>
                    </Box>
                );

                // Tax name gets its own tooltip trigger — wraps only the Typography now,
                // not the checkbox, so hovering the name alone shows the card.
                const taxNameEl = customizedTooltipContent ? (
                    <HoverTaxTooltip placement="left" arrow title={customizedTooltipContent}>
                        <Typography sx={TAX_NAME_SX}>{row.name}</Typography>
                    </HoverTaxTooltip>
                ) : (
                    <Typography sx={TAX_NAME_SX}>{row.name}</Typography>
                );

                // Checkbox gets its own separate tooltip trigger. When the checkbox is
                // disabled (customized), it shows the "edited on line items" explanation
                // instead of the breakdown card — same style, different message.
                const checkboxTooltipTitle = customized ? disabledCheckboxTooltipContent : customizedTooltipContent;

                const checkboxEl = checkboxTooltipTitle ? (
                    <HoverTaxTooltip placement="left" arrow title={checkboxTooltipTitle}>
                        {/* span wrapper: CheckBoxV4 can be disabled, and Tooltip
                needs a non-disabled child to attach hover listeners to */}
                        <span>
                            <CheckBoxV4 checked={!!row.applyTax} disabled={customized} onChange={(e) => handleApplyTaxChange(index, e.target.checked)} />
                        </span>
                    </HoverTaxTooltip>
                ) : (
                    <CheckBoxV4 checked={!!row.applyTax} disabled={customized} onChange={(e) => handleApplyTaxChange(index, e.target.checked)} />
                );

                const amountEl = customizedTooltipContent ? (
                    <HoverTaxTooltip placement="left" arrow title={customizedTooltipContent}>
                        <Typography sx={TAX_AMOUNT_SX}>
                            {Number(row.taxAmount) > 0
                                ? NumericEngine.format(row.taxAmount, {
                                      formatCurrency: true,
                                      trailingZeros: true,
                                  })
                                : '$0.00'}
                        </Typography>
                    </HoverTaxTooltip>
                ) : (
                    <Typography sx={TAX_AMOUNT_SX}>
                        {Number(row.taxAmount) > 0
                            ? NumericEngine.format(row.taxAmount, {
                                  formatCurrency: true,
                                  trailingZeros: true,
                              })
                            : '$0.00'}
                    </Typography>
                );

                return (
                    // Fragment (not a Box/div) is required here — grid columns only track
                    // across cells that are DIRECT children of the grid container. Wrapping
                    // a row in its own Box would create a single grid item per row instead
                    // of six aligned cells.
                    <Fragment key={row.taxId || `${row.name}-${index}`}>
                        <Box sx={!hideCheckbox ? TAX_NAME_CELL_SX : { width: 'max-content' }}>{taxNameEl}</Box>

                        {!hideCheckbox ? checkboxEl : <div></div>}

                        {!hideCheckbox ? <Divider orientation="vertical" flexItem sx={DIVIDER_SX} /> : <div></div>}

                        {editable ? (
                            <TextField
                                inputRef={index === refIndex ? taxPercentageInputRef : null}
                                disabled={isDisabled}
                                value={displayRateInput(row[rateKey], precision)}
                                onChange={(e) => handleRateChange(index, e.target.value)}
                                sx={{
                                    ...TAX_INPUT_SX,
                                    ...(isDisabled ? DISABLED_TAX_INPUT_SX : {}),
                                }}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                }}
                            />
                        ) : (
                            <Typography sx={TAX_RATE_LABEL_SX}>{formatTaxPercent(getTaxRowRate(row, documentType), precision)}%</Typography>
                        )}
                        <Typography>-</Typography>
                        {amountEl}
                    </Fragment>
                );
            })}
            {taxesBreakdown?.length > 1 && (
                // Total row spans every grid column and lays itself out with its own
                // flex space-between, independent of the per-tax column tracks above.
                <Box sx={{ gridColumn: '1 / -1', ...RIGHT_SECTION_SX }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 400, color: 'rgba(0, 0, 0, 0.6)' }}>Total Tax</Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 400, color: 'rgba(0, 0, 0, 0.6)' }}>{fmtMoney(totalTax)}</Typography>
                </Box>
            )}
        </Box>
    );
};

export default MultiTaxRows;
